// PythonFormatter.js
export const formatPython = (code, tabSize = 4) => {
  try {
    if (!code) return { convertedData: '', isError: false };

    const lines = code.split('\n');

    // Detect the smallest indentation unit in the source so we can normalize it.
    // Tabs are treated as 4 spaces for detection purposes.
    let minIndent = Infinity;
    for (const line of lines) {
      if (!line.trim()) continue;
      let indent = 0;
      for (const ch of line) {
        if (ch === ' ') indent++;
        else if (ch === '\t') indent += 4;
        else break;
      }
      if (indent > 0) minIndent = Math.min(minIndent, indent);
    }
    // If all lines are at column 0 (no indentation detected), keep them as-is.
    const hasIndent = isFinite(minIndent);

    const formatted = lines.map(line => {
      if (!line.trim()) return '';

      // Measure raw leading indent (tabs count as 4 spaces)
      let rawIndent = 0;
      let i = 0;
      for (; i < line.length; i++) {
        if (line[i] === ' ') rawIndent++;
        else if (line[i] === '\t') rawIndent += 4;
        else break;
      }

      const content = line.slice(i).trimEnd();

      if (!hasIndent) {
        // No indentation in source — return content as-is (can't reliably re-indent
        // without a Python AST parser)
        return content;
      }

      // Normalize: map existing indent level → tabSize spaces per level
      const level = Math.round(rawIndent / minIndent);
      return ' '.repeat(level * tabSize) + content;
    });

    // Collapse 3+ consecutive blank lines to 2, trim leading/trailing whitespace
    const result = formatted
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return { convertedData: result, isError: false };
  } catch (err) {
    return { convertedData: 'Error: Invalid Python code', isError: true };
  }
};


// TypeScriptFormatter.js
export const formatTypeScript = (code, tabSize = 2) => {
  try {
    if (!code) return { convertedData: '', isError: false };

    // Step 1: Extract all string/template literals and replace with placeholders
    // so that { } ; inside strings are never touched by the formatter.
    const strings = [];
    let processed = code;

    // Template literals first (they may contain quotes internally)
    processed = processed.replace(/`(?:[^`\\]|\\.)*`/g, (match) => {
      strings.push(match);
      return `__STR${strings.length - 1}__`;
    });
    // Double-quoted strings
    processed = processed.replace(/"(?:[^"\\]|\\.)*"/g, (match) => {
      strings.push(match);
      return `__STR${strings.length - 1}__`;
    });
    // Single-quoted strings
    processed = processed.replace(/'(?:[^'\\]|\\.)*'/g, (match) => {
      strings.push(match);
      return `__STR${strings.length - 1}__`;
    });

    // Step 2: Format only the structural code.
    // Use [ \t]* (not \s*) for { } ; so that existing newlines in multi-line
    // input are not swallowed and multi-line comma-separated items stay on
    // separate lines.
    let formatted = processed
      .replace(/;[ \t]*/g, ';\n')
      .replace(/\{[ \t]*/g, '{\n')
      .replace(/\}[ \t]*/g, '\n}\n')
      .replace(/,[ \t]*/g, ', ')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line !== '')
      .join('\n');

    // Step 3: Apply indentation based on braces
    let indent = 0;
    const indented = formatted.split('\n').map(line => {
      if (line.startsWith('}')) indent = Math.max(indent - 1, 0);
      const indentedLine = ' '.repeat(tabSize * indent) + line;
      if (line.endsWith('{')) indent++;
      return indentedLine;
    }).join('\n');

    // Step 4: Add a blank line after each top-level closing brace (indent = 0)
    // so declarations are visually separated.
    const withSpacing = indented.replace(/^}/mg, '}\n');

    // Step 5: Restore the original string/template literal content
    const result = withSpacing
      .replace(/__STR(\d+)__/g, (_, idx) => strings[parseInt(idx)])
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return { convertedData: result, isError: false };
  } catch (err) {
    return { convertedData: 'Error: Invalid TypeScript code', isError: true };
  }
};

export const formatCss = (css) => {
    const lines = css
        .replace(/\s*{\s*/g, " {\n  ")
        .replace(/\s*}\s*/g, "\n}\n")
        .replace(/\s*;\s*/g, ";\n  ")
        .split('\n');

    // Only normalise ':' on indented lines (property declarations inside a rule block).
    // Selector lines like 'a:hover {' and URL values like 'url(https://...)' are NOT indented,
    // so they are left untouched.
    return lines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';           // whitespace-only lines (artifact of ;\n  before }) → blank
        if (trimmed === '}') return '}';   // de-indent any } that picked up leading spaces
        if (/^\s+/.test(line)) {
            return line.replace(/^(\s*)([\w-]+)\s*:\s*(?!\/\/)/, '$1$2: ');
        }
        return line;
    }).join('\n').replace(/\n\s*\n}/g, '\n}').replace(/\n{3,}/g, '\n\n').trim();
};
